import { Injectable } from '@angular/core';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Swipe from '@arcgis/core/widgets/Swipe';
import { SwipePresetId } from '../types/map.types';
import { SWIPE_PRESETS, SwipePreset, SwipeLayerSpec } from '../types/swipe-preset.types';

export interface SwipeLayerInputs {
  leadingLayers: __esri.Layer[];
  trailingLayers: __esri.Layer[];
  direction?: 'horizontal' | 'vertical';
  initialPosition?: number;
}

@Injectable({ providedIn: 'root' })
export class MapSwipeService {
  private swipeWidget: Swipe | null = null;
  private addedLayers: FeatureLayer[] = [];

  getPreset(id: SwipePresetId): SwipePreset | null {
    if (id === 'off' || id === 'project-design-vs-asbuilt') {
      return null;
    }
    return SWIPE_PRESETS[id] || null;
  }

  isActive(): boolean {
    return this.swipeWidget !== null;
  }

  /** Match hit inspection to the exact half of the map shown by the current divider. */
  isLayerVisibleAt(layer: __esri.Layer, x: number, y: number, view: __esri.MapView): boolean {
    const widget = this.swipeWidget;
    if (!widget || widget.view !== view) return true;
    const leading = widget.direction === 'horizontal' ? x <= view.width * widget.position / 100 : y <= view.height * widget.position / 100;
    if (widget.leadingLayers.includes(layer)) return leading;
    if (widget.trailingLayers.includes(layer)) return !leading;
    return true;
  }

  activate(mapView: __esri.MapView, presetId: SwipePresetId): void {
    this.deactivate(mapView);
    const preset = this.getPreset(presetId);
    if (!preset) {
      return;
    }

    const leading = preset.leadingLayers.map(spec => {
      const layer = new FeatureLayer({ url: spec.url, title: spec.title });
      this.attachLoadErrorHandler(layer, spec);
      return layer;
    });
    const trailing = preset.trailingLayers.map(spec => {
      const layer = new FeatureLayer({ url: spec.url, title: spec.title });
      this.attachLoadErrorHandler(layer, spec);
      return layer;
    });

    mapView.map.addMany([...leading, ...trailing]);
    this.addedLayers = [...leading, ...trailing];

    this.swipeWidget = new Swipe({
      view: mapView,
      leadingLayers: leading as unknown as __esri.Collection<__esri.Layer>,
      trailingLayers: trailing as unknown as __esri.Collection<__esri.Layer>,
      direction: preset.direction,
      position: preset.initialPosition,
    });

    mapView.ui.add(this.swipeWidget);
  }

  /**
   * Activate Swipe over layers that are already on the map (e.g. project reference
   * layers managed by MapReferenceLayerService). Unlike activate(), this does not
   * add or remove layers — it only wraps the existing ones in a Swipe widget.
   */
  activateWithLayers(mapView: __esri.MapView, inputs: SwipeLayerInputs): void {
    this.deactivate(mapView);
    if (!inputs.leadingLayers.length || !inputs.trailingLayers.length) {
      // eslint-disable-next-line no-console
      console.warn('[MapSwipeService] activateWithLayers called with empty side',
        { leading: inputs.leadingLayers.length, trailing: inputs.trailingLayers.length });
      return;
    }
    this.swipeWidget = new Swipe({
      view: mapView,
      leadingLayers: inputs.leadingLayers as unknown as __esri.Collection<__esri.Layer>,
      trailingLayers: inputs.trailingLayers as unknown as __esri.Collection<__esri.Layer>,
      direction: inputs.direction || 'horizontal',
      position: inputs.initialPosition ?? 50,
    });
    mapView.ui.add(this.swipeWidget);
  }

  deactivate(mapView: __esri.MapView): void {
    if (this.swipeWidget) {
      mapView.ui.remove(this.swipeWidget);
      this.swipeWidget.destroy();
      this.swipeWidget = null;
    }
    if (this.addedLayers.length) {
      mapView.map.removeMany(this.addedLayers);
      this.addedLayers = [];
    }
  }

  /**
   * External FeatureServer URLs can 404, time out, or fail auth. Without this
   * handler the Swipe divider silently shows an empty side. We log to
   * console.error so production Sentry's console-error capture picks it up.
   */
  private attachLoadErrorHandler(layer: FeatureLayer, spec: SwipeLayerSpec): void {
    layer.on('layerview-create-error', event => {
      console.error(`[MapSwipeService] Failed to load swipe layer "${spec.title}" at ${spec.url}`, event.error);
    });
  }
}
